// WWI field kitchen — the goulash cannon and its mess line. A wheat-gold
// canvas fly (the food code from above) shelters the prep table across the
// back; the ICONIC feature is the field stove out on open ground — a gunmetal
// boiler slung between two spoked wheels with a tall stovepipe and a smoke
// puff. A long trestle table with two benches fills the open front where the
// troops sit and eat.
import * as THREE from "three";
import { defineModel, benchWork, sitEat, styleV3, type Part } from "../../lib";

const { box, cyl, gable, poles, slab, ROOF_FOOD } = styleV3;

// Muted WWI materials.
const KHAKI = "#7a6f4e";
const GUNMETAL = "#4a4d52";
const TIMBER_W = "#8b6438";
const MUD = "#5d4a33";
const CANVAS_W = "#cbbd93";
const SMOKE = "#c9c5bd";

const BASE = 0.12;                  // foundation slab top
const AWN_Z = -0.58;                // awning centre z (back of the plot)
const AWN_W = 1.5, AWN_D = 0.7;
const AWN_H = 0.6;                  // awning pole height
const STOVE_X = 0.55, STOVE_Z = 0.05;   // the goulash cannon, open ground mid-east
const TABLE_X = -0.25, TABLE_Z = 0.52;  // the long mess table, open front
const BENCH_DZ = 0.26;              // bench offset from the table centre
const SEAT_TOP = BASE + 0.135;      // bench seat height (matches the canteen)

// The awning's four timber poles.
function awnPoles(h: number): Part[] {
  const out: Part[] = [];
  for (const [x, z] of [[-AWN_W / 2, AWN_Z - AWN_D / 2], [AWN_W / 2, AWN_Z - AWN_D / 2],
                        [-AWN_W / 2, AWN_Z + AWN_D / 2], [AWN_W / 2, AWN_Z + AWN_D / 2]] as const) {
    out.push(cyl(0.03, 0.035, h, 5, TIMBER_W, [x, BASE + h / 2, z]));
  }
  return out;
}

// The wheat-gold canvas fly over the prep area — ONE shallow gable.
function fly(): Part {
  return gable(AWN_D + 0.24, AWN_W + 0.24, 0.18, ROOF_FOOD, 0, BASE + AWN_H, AWN_Z, "x");
}

// The prep table and stores under the fly.
function prep(): Part[] {
  return [
    box(0.7, 0.05, 0.26, TIMBER_W, [-0.25, BASE + 0.3, AWN_Z]),
    box(0.08, 0.28, 0.2, MUD, [-0.52, BASE + 0.14, AWN_Z]),
    box(0.08, 0.28, 0.2, MUD, [0.02, BASE + 0.14, AWN_Z]),
    // A stock pot and a ration sack beside the table.
    cyl(0.09, 0.08, 0.12, 8, GUNMETAL, [0.42, BASE + 0.06, AWN_Z + 0.08]),
    { geometry: new THREE.SphereGeometry(0.1, 7, 5), color: CANVAS_W,
      position: [0.55, BASE + 0.065, AWN_Z - 0.15], scale: [1.1, 0.62, 0.9] },
  ];
}

// The ICONIC goulash cannon: boiler between two spoked wheels, firebox,
// tow bar and a tall stovepipe with a smoke puff.
function stove(): Part[] {
  return [
    // Two wagon wheels.
    cyl(0.16, 0.16, 0.045, 10, TIMBER_W, [STOVE_X - 0.17, BASE + 0.16, STOVE_Z], [0, 0, Math.PI / 2]),
    cyl(0.16, 0.16, 0.045, 10, TIMBER_W, [STOVE_X + 0.17, BASE + 0.16, STOVE_Z], [0, 0, Math.PI / 2]),
    // The boiler slung between them, lid on top.
    cyl(0.13, 0.13, 0.3, 8, GUNMETAL, [STOVE_X, BASE + 0.24, STOVE_Z], [0, 0, Math.PI / 2]),
    cyl(0.09, 0.09, 0.04, 8, KHAKI, [STOVE_X, BASE + 0.37, STOVE_Z]),
    // Firebox door glow at the front face.
    box(0.08, 0.07, 0.02, "#ff7a2e", [STOVE_X, BASE + 0.18, STOVE_Z + 0.13]),
    // Tow bar trailing off the axle, away from the cook's spot.
    box(0.04, 0.03, 0.3, TIMBER_W, [STOVE_X, BASE + 0.12, STOVE_Z - 0.28], [0.25, 0, 0]),
    // The tall stovepipe and its smoke puff — the read from above.
    cyl(0.035, 0.045, 0.5, 6, GUNMETAL, [STOVE_X - 0.08, BASE + 0.56, STOVE_Z]),
    { geometry: new THREE.SphereGeometry(0.085, 7, 5), color: SMOKE,
      position: [STOVE_X - 0.08, BASE + 0.9, STOVE_Z], scale: [1, 0.8, 1] },
    { geometry: new THREE.SphereGeometry(0.06, 6, 5), color: SMOKE,
      position: [STOVE_X - 0.02, BASE + 1.02, STOVE_Z - 0.04] },
  ];
}

// The long mess table with a bench either side, in the open front.
function messTable(): Part[] {
  const out: Part[] = [
    box(0.9, 0.05, 0.28, TIMBER_W, [TABLE_X, BASE + 0.27, TABLE_Z]),
    box(0.1, 0.24, 0.22, MUD, [TABLE_X - 0.32, BASE + 0.12, TABLE_Z]),
    box(0.1, 0.24, 0.22, MUD, [TABLE_X + 0.32, BASE + 0.12, TABLE_Z]),
  ];
  for (const dz of [-BENCH_DZ, BENCH_DZ]) {
    out.push(
      box(0.86, 0.05, 0.13, TIMBER_W, [TABLE_X, SEAT_TOP, TABLE_Z + dz]),
      box(0.08, 0.1, 0.1, MUD, [TABLE_X - 0.3, BASE + 0.05, TABLE_Z + dz]),
      box(0.08, 0.1, 0.1, MUD, [TABLE_X + 0.3, BASE + 0.05, TABLE_Z + dz]),
    );
  }
  // Mess tins on the table.
  out.push(
    cyl(0.045, 0.045, 0.03, 7, GUNMETAL, [TABLE_X - 0.22, BASE + 0.31, TABLE_Z]),
    cyl(0.045, 0.045, 0.03, 7, GUNMETAL, [TABLE_X + 0.15, BASE + 0.31, TABLE_Z]),
  );
  return out;
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(1.94, 1.94)];
  if (s === 1) return [slab(1.94, 1.94), ...awnPoles(0.3), ...poles(1.8, 1.8, 0.7)];
  if (s === 2) {
    return [slab(1.94, 1.94), ...awnPoles(AWN_H), ...prep().slice(0, 3), ...poles(1.8, 1.8, 1.1)];
  }
  return [
    slab(1.94, 1.94),
    ...awnPoles(AWN_H),
    fly(),
    ...prep(),
    ...stove(),
    ...messTable(),
    // A water barrel at the yard edge.
    cyl(0.11, 0.1, 0.24, 8, MUD, [-0.78, BASE + 0.12, 0.02]),
  ];
}

export const fieldKitchen = defineModel({
  id: "field-kitchen",
  tiles: { w: 2, h: 2 },
  batchId: "wwi",
  name: "Field Kitchen",
  tags: ["building", "wwi", "military", "kitchen", "food", "mess"],
  summary: "A canvas-flied cook station with a smoking goulash cannon and a mess table.",
  description:
    "A field kitchen: a wheat-gold canvas fly over the prep table, the iconic " +
    "wheeled field stove (the goulash cannon) smoking on open ground, and a " +
    "long trestle mess table with benches where the troops sit and eat.",
  author: "Fable 5",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "foundation", label: "Foundation", parts: () => partsFor(0) },
    { id: "lower", label: "Lower", parts: () => partsFor(1) },
    { id: "upper", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "cook", position: [STOVE_X, 0.12, STOVE_Z + 0.42], facing: Math.PI, description: "The cook's spot at the goulash cannon." },
    { id: "eat", position: [TABLE_X, 0.28, TABLE_Z + BENCH_DZ], facing: Math.PI, description: "A bench seat at the mess table." },
  ],
  interactions: [
    { id: "cook-stew", label: "Cooking", station: "cook", variants: ["complete"], ...benchWork() },
    { id: "eat-meal", label: "Eating", station: "eat", variants: ["complete"], ...sitEat() },
  ],
});
